<script setup>
import { ref, watch } from 'vue'
import { useTagsStore } from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'
import { mediaUrl } from '@core/utils/mediaUrl'

const props = defineProps({
  tagId: {
    type: String,
    default: null
  },
  tagName: {
    type: String,
    default: ''
  },
  isDialogVisible: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:isDialogVisible'])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth(700)

const tagsStore = useTagsStore()

const activeMembers = ref([])
const loading = ref(false)
const count = ref(0)

const updateModelValue = val => {
  emit('update:isDialogVisible', val)
}

watch(() => props.isDialogVisible, async newValue => {
  if (newValue && props.tagId) {
    loading.value = true
    try {
      const data = await tagsStore.getActiveMembers(props.tagId)
      activeMembers.value = data.members || []
      count.value = data.count || 0
      console.log('Active members data:', data)
    } catch (error) {
      console.error('Error fetching active members:', error)
      activeMembers.value = []
      count.value = 0
    } finally {
      loading.value = false
    }
  }
})

const goToProfile = profileId => {
  window.location.href = `/m/${profileId}`
}
</script>

<template>
  <VDialog 
    :model-value="isDialogVisible" 
    :width="dWidth" 
    max-width="700"
    @update:model-value="updateModelValue"
  >
    <VCard class="pa-sm-9 pa-5">
      <VCardItem class="text-center">
        <VCardTitle class="text-h5">
          {{ tagName }} - Members
        </VCardTitle>
        <VCardSubtitle>
          {{ count }} current Platinum member{{ count !== 1 ? 's' : '' }} with this verified tag
        </VCardSubtitle>
      </VCardItem>
            
      <VCardText>
        <div
          v-if="loading"
          class="text-center py-8"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
          <p class="mt-4">
            Loading members...
          </p>
        </div>
                
        <div
          v-else-if="activeMembers.length === 0"
          class="text-center py-8"
        >
          <p>No members found for this tag.</p>
        </div>
                
        <VList v-else>
          <VListItem
            v-for="member in activeMembers"
            :key="member._id"
            class="mb-2"
            style="cursor: pointer"
            @click="goToProfile(member.profileId)"
          >
            <template #prepend>
              <VAvatar 
                v-if="member.profileImageId"
                size="40"
              >
                <VImg
                  :src="mediaUrl(member.profileImageId, 'thumb')"
                  :alt="member.fullName"
                />
              </VAvatar>
              <VAvatar 
                v-else
                size="40"
                color="primary"
              >
                <span class="text-white">{{ member.firstName?.charAt(0) }}{{ member.lastName?.charAt(0) }}</span>
              </VAvatar>
            </template>
                        
            <VListItemTitle>
              {{ member.fullName }}
            </VListItemTitle>
                        
            <VListItemSubtitle>
              {{ member.email }}
            </VListItemSubtitle>
                        
            <template #append>
              <VIcon>mdi-chevron-right</VIcon>
            </template>
          </VListItem>
        </VList>
      </VCardText>
            
      <VCardActions class="justify-center">
        <VBtn
          color="secondary"
          variant="tonal"
          @click="emit('update:isDialogVisible', false)"
        >
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.v-list-item:hover {
    background-color: rgba(0, 0, 0, 0.04);
}
</style>

